import React from 'react'
import { Partner } from '..'
import classNames from 'classnames'
import { Button, TextSpinner } from 'ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faSpinner, faNoteSticky } from '@fortawesome/free-solid-svg-icons'
import { faClock } from '@fortawesome/free-regular-svg-icons'
import { useIntl } from 'react-intl'
import { useAppSelector } from '../../../store/hooks'
import { Link, useNavigate } from 'react-router-dom'
import { createDateFromISO, temp_createEventDateFromISO } from 'utils/dates'
import Breadcrumb from '../../common/Breadcrumb'
import { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger } from 'ui/Tooltip'
import { useGetLatestPartnerNote } from '../../../hooks/useGetLatestPartnerNote/useGetLatestPartnerNote'
type Codes = 'operations' | 'info'

type Props = {
   partner?: Partner
   partnerStatus: string
   accountCode?: string
   id?: string
   children?: React.ReactNode
   activeTabCode: Codes
}

const PartnerTemplate = ({ partner, partnerStatus, accountCode, id, children, activeTabCode }: Props) => {
   const intl = useIntl()
   const { locale } = useAppSelector((state) => state.base)
   const navigate = useNavigate()
   const { data: latestNote } = useGetLatestPartnerNote(id)

   function drawTabs(items: Array<{ code: Codes; title: string; url: string }> = [], active: number = 0) {
      return (
         <div className="m-4 pl-4 border-b border-gray-300 flex justify-between items-center">
            <ul className="flex flex-wrap text-center text-gray-500">
               {items.map((item, index) => (
                  <li key={item.title} className="me-2">
                     <Link
                        to={`/${accountCode}/partners/${id}${item.url}`}
                        aria-current="page"
                        className={classNames('inline-block px-4 py-2 rounded-t border border-gray-300', {
                           'border-b-gray-100 -mb-1': item.code === activeTabCode,
                           'border-b-0 bg-gray-50 hover:text-gray-600 hover:bg-gray-50': item.code !== activeTabCode,
                        })}
                     >
                        {item.title}
                     </Link>
                  </li>
               ))}
            </ul>
            {partner && accountCode && id && (
               // <Button.Group className="-mt-2 flex items-center" spaced={false}>
               <Button
                  onClick={() => {
                     navigate(`/${accountCode}/partners/${id}/operation/create`)
                  }}
                  className="-mt-2"
                  bsStyle="primary"
               >
                  <div className="flex gap-x-2 items-center">
                     <FontAwesomeIcon icon={faPlus} className={'text-white'} />
                     {intl.formatMessage({ id: 'operations.new' })}
                  </div>
               </Button>
               /* <Dropdown className="border rounded-md bg-amber-500 text-white border-amber-500 border-l-gray-100">
                     <Dropdown.Toggle className="cursor-pointer inline-block leading-normal py-1.5 px-3 font-medium focus:outline-none disabled:opacity-50 text-center"></Dropdown.Toggle>
                     <Dropdown.Menu className="right-0">
                        <ul>
                           <li className="whitespace-nowrap py-2 px-4 font-bold">Transferir puntos</li>
                           <li className="whitespace-nowrap py-2 px-4">Desde esta cuenta</li>
                           <li className="whitespace-nowrap py-2 px-4">Hacia esta cuenta</li>
                        </ul>
                     </Dropdown.Menu>
                  </Dropdown> */
               /* </Button.Group> */
            )}
         </div>
      )
   }

   const breadcrumb = [
      {
         url: `/${accountCode}/partners/`,
         label: intl.formatMessage({ id: 'partner.operation.members' }),
      },
      {
         label: partner?.externalId?.toString(),
      },
      ...(activeTabCode !== 'info' ? [{ label: intl.formatMessage({ id: `partner.titles.${activeTabCode}` }) }] : []),
   ]
   return (
      <>
         <Breadcrumb items={breadcrumb} className="m-4" />
         {partnerStatus === 'pending' ? (
            <div className="absolute inset-0 bg-neutral-400 opacity-25 flex items-center justify-center">
               <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            </div>
         ) : null}
         {partner ? (
            <div className="flex justify-between m-4">
               <div>
                  <h2 className="text-3xl mb-1 flex items-center flex-wrap gap-2">
                     {`${partner.firstName} ${partner.lastName}`}
                     {partner.isActive ? null : (
                        <small className="bg-red-600 text-white font-bold text-xs p-1 rounded-sm">
                           {intl.formatMessage({ id: 'partner.status.desactivate' })}
                        </small>
                     )}
                     {latestNote && (
                        <TooltipProvider delayDuration={0}>
                           <Tooltip>
                              <TooltipTrigger asChild>
                                 <button className="inline-flex items-center gap-x-1.5 border border-amber-500 text-amber-600 text-sm font-normal rounded px-2 py-1 max-w-[220px]">
                                    <FontAwesomeIcon icon={faNoteSticky} size="xs" />
                                    <span className="truncate">{latestNote.content}</span>
                                 </button>
                              </TooltipTrigger>
                              <TooltipContent className="w-64 p-3 bg-white border border-amber-500 text-sm text-gray-700 shadow-md">
                                 <TooltipArrow className="fill-amber-500" />
                                 <div className="flex items-start gap-x-2">
                                    <FontAwesomeIcon icon={faNoteSticky} className="text-amber-500 mt-0.5 shrink-0" />
                                    <div>
                                       <p className="mb-2">{latestNote.content}</p>
                                       <p className="text-gray-500 text-xs">
                                          {intl.formatMessage({ id: 'partner.notes.by' }, { author: latestNote.authorName })}
                                       </p>
                                       <p className="text-gray-400 text-xs mt-1">
                                          <FontAwesomeIcon icon={faClock} size="xs" className="mr-1" />
                                          {(() => {
                                             try {
                                                return temp_createEventDateFromISO(latestNote.createdAt).setLocale(locale).toRelative()
                                             } catch {
                                                return latestNote.createdAt
                                             }
                                          })()}
                                       </p>
                                    </div>
                                 </div>
                              </TooltipContent>
                           </Tooltip>
                        </TooltipProvider>
                     )}
                  </h2>

                  <div className="text-neutral-400">
                     {partner.createdAt &&
                        intl.formatMessage(
                           { id: 'partner.from' },
                           {
                              date: createDateFromISO(partner.createdAt)
                                 .setLocale(locale)
                                 .toFormat('EEEE, dd MMMM yyyy'),
                           },
                        )}
                  </div>
               </div>
               <div className="text-right">
                  <h2 className="text-3xl mb-1">{partner.externalId}</h2>
                  <div className="text-neutral-400">ID de socio</div>
               </div>
            </div>
         ) : (
            <div className="m-4">
               <h2 className="text-3xl mb-4">
                  <TextSpinner
                     style={{
                        width: '50%',
                        color: 'transparent',
                        lineHeight: '1.0667em',
                     }}
                  >
                     .
                  </TextSpinner>
               </h2>

               <TextSpinner
                  style={{
                     width: '35%',
                  }}
                  className="leading-none text-transparent mb-2"
               >
                  .
               </TextSpinner>
               <TextSpinner
                  style={{
                     width: '35%',
                  }}
                  className="leading-none text-transparent mb-2"
               >
                  .
               </TextSpinner>
            </div>
         )}
         {drawTabs(
            [
               {
                  code: 'operations',
                  title: intl.formatMessage({ id: 'partner.titles.operations' }),
                  url: '/operations',
               },
               { code: 'info', title: intl.formatMessage({ id: 'partner.titles.info' }), url: '' },
            ],
            0,
         )}
         {children}
      </>
   )
}

export default PartnerTemplate
